import React, { useState, useEffect, useContext } from 'react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { CreditCard, Crown, CheckCircle } from 'lucide-react';
import { AuthContext } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import Price from '../Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionButton = ({ service }) => {
  const { user } = useContext(AuthContext);
  const { formatPrice } = useCurrency();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentSettings, setPaymentSettings] = useState(null);

  useEffect(() => {
    if (user) {
      checkSubscription();
      fetchPaymentSettings();
    }
  }, [user, service]);

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings-public`);
      setPaymentSettings(response.data);
      if (response.data.stripe?.enabled) {
        setPaymentMethod('stripe');
      } else if (response.data.paypal?.enabled) {
        setPaymentMethod('paypal');
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/tipster-services/${service.id}/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSubscribe = () => {
    if (!user) {
      toast.error('Please login to subscribe');
      return;
    }
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    setLoading(true);
    setShowPaymentModal(false);
    
    const token = localStorage.getItem('session_token');
    const originUrl = window.location.origin;
    
    try {
      const endpoint = paymentMethod === 'stripe'
        ? `/payments/service-subscription/stripe/checkout?service_id=${service.id}&origin_url=${encodeURIComponent(originUrl)}`
        : `/payments/service-subscription/paypal/checkout?service_id=${service.id}&origin_url=${encodeURIComponent(originUrl)}`;
      
      const response = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const redirectUrl = response.data.url || response.data.approval_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        toast.error('Payment URL not received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start payment');
      setLoading(false);
    }
  };

  // Don't show if no subscription price or price is 0
  if (!service.subscription_price || service.subscription_price <= 0) {
    return null;
  }

  // Show subscribed status
  if (subscriptionStatus?.subscribed) {
    return (
      <Card className="glass-dark border-green-500/30 bg-green-900/10 mb-6">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div>
              <p className="text-green-400 font-semibold">Subscribed</p>
              <p className="text-gray-400 text-sm">
                Active until {new Date(subscriptionStatus.subscription.end_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="glass-dark border-blue-500/30 bg-blue-900/10 mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Crown className="w-6 h-6 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-white font-bold text-lg">Monthly Subscription</p>
                <p className="text-gray-400 text-sm mt-1">
                  Get unlimited access to ALL tips from this service
                </p>
                <p className="text-blue-400 font-semibold mt-2">
                  Only <Price amount={service.subscription_price} />/month
                </p>
              </div>
            </div>
            <Button
              onClick={handleSubscribe}
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-6 text-lg whitespace-nowrap"
            >
              <Crown className="w-5 h-5 mr-2" />
              {loading ? 'Processing...' : `Subscribe ${formatPrice(service.subscription_price)}/month`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Subscribe to Service
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPaymentModal(false)}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white/5 rounded p-4">
                <p className="text-gray-400 text-sm">Monthly Subscription</p>
                <p className="text-3xl font-bold text-blue-400">
                  <Price amount={service.subscription_price} />
                </p>
                <p className="text-gray-400 text-sm mt-2">Unlimited access to all tips</p>
              </div>
              
              <div>
                <p className="text-white font-semibold mb-3">Payment Method:</p>
                <div className="space-y-2">
                  {paymentSettings?.stripe?.enabled && (
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'stripe'
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'stripe' ? 'border-blue-500' : 'border-white/30'
                        }`}>
                          {paymentMethod === 'stripe' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                        <span className="text-white font-medium">Credit/Debit Card (Stripe)</span>
                      </div>
                    </button>
                  )}
                  
                  {paymentSettings?.paypal?.enabled && (
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'paypal'
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'paypal' ? 'border-blue-500' : 'border-white/30'
                        }`}>
                          {paymentMethod === 'paypal' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                        <span className="text-white font-medium">PayPal</span>
                      </div>
                    </button>
                  )}
                </div>
              </div>
              
              <Button
                onClick={confirmPayment}
                className="bg-blue-600 hover:bg-blue-700 w-full text-lg py-6"
              >
                Proceed to Payment
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default SubscriptionButton;